import { Utils } from '@gv/triage-components';
import { createApi } from '@reduxjs/toolkit/query/react';

import { URL } from 'api/constants';
import { apiQuery } from 'store/query';

import {
  UploadFileRequest,
  UploadFileResponse,
  GetSessionsRequest,
  GetSessionResponse,
  GetSessionsResponse,
  GetTemplatesRequest,
  GetTemplateResponse,
  GetTemplatesResponse,
  ProcessSessionRequest,
  CreateTemplateRequest,
  UpdateTemplateRequest,
  UploadRecordingRequest,
  ProcessSessionResponse,
  CreateTemplateResponse,
  UpdateTemplateResponse,
  UploadRecordingResponse,
} from './types';

export const aiScribeApi = createApi({
  baseQuery: apiQuery,
  reducerPath: 'aiScribeApi',
  tagTypes: ['Sessions', 'Templates'],
  endpoints: (build) => ({
    processSession: build.mutation<
      ProcessSessionResponse,
      ProcessSessionRequest
    >({
      invalidatesTags: ['Sessions'],
      query: (request) => {
        return {
          data: request,
          method: 'post',
          url: URL.AI_SCRIBE_PROCESS,
        };
      },
    }),

    createTemplate: build.mutation<
      CreateTemplateResponse,
      CreateTemplateRequest
    >({
      invalidatesTags: ['Templates'],
      query: (request) => {
        return {
          data: request,
          method: 'post',
          url: URL.AI_SCRIBE_TEMPLATES,
        };
      },
    }),

    deleteSession: build.mutation<{ success: boolean }, number>({
      invalidatesTags: ['Sessions'],
      query: (sessionId) => {
        const url = URL.AI_SCRIBE_SESSION.replace(
          ':sessionId',
          sessionId.toString()
        );
        return {
          url,
          method: 'delete',
        };
      },
    }),

    deleteTemplate: build.mutation<{ success: boolean }, number>({
      invalidatesTags: ['Templates'],
      query: (templateId) => {
        const url = URL.AI_SCRIBE_TEMPLATE.replace(
          ':templateId',
          templateId.toString()
        );
        return {
          url,
          method: 'delete',
        };
      },
    }),

    getSession: build.query<GetSessionResponse, number>({
      transformResponse: (response: GetSessionResponse) => {
        return response;
      },
      providesTags: (result, error, sessionId) => [
        { id: sessionId, type: 'Sessions' },
      ],
      query: (sessionId) => {
        const url = URL.AI_SCRIBE_SESSION.replace(
          ':sessionId',
          sessionId.toString()
        );
        return {
          url,
          method: 'get',
        };
      },
    }),

    getTemplate: build.query<GetTemplateResponse, number>({
      transformResponse: (response: GetTemplateResponse) => {
        return response;
      },
      providesTags: (result, error, templateId) => [
        { id: templateId, type: 'Templates' },
      ],
      query: (templateId) => {
        const url = URL.AI_SCRIBE_TEMPLATE.replace(
          ':templateId',
          templateId.toString()
        );
        return {
          url,
          method: 'get',
        };
      },
    }),

    updateTemplate: build.mutation<
      UpdateTemplateResponse,
      UpdateTemplateRequest
    >({
      invalidatesTags: (result, error, { template_id }) => [
        'Templates',
        { id: template_id, type: 'Templates' },
      ],
      query: ({ template_id, ...data }) => {
        const url = URL.AI_SCRIBE_TEMPLATE.replace(
          ':templateId',
          template_id.toString()
        );
        return {
          url,
          data,
          method: 'put',
        };
      },
    }),

    uploadFile: build.mutation<UploadFileResponse, UploadFileRequest>({
      invalidatesTags: ['Sessions'],
      query: ({ file, hospital_id, template_id }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('hospital_id', hospital_id.toString());
        if (template_id) {
          formData.append('template_id', template_id.toString());
        }

        return {
          method: 'post',
          data: formData,
          url: URL.AI_SCRIBE_UPLOAD_FILE,
        };
      },
    }),

    getTemplates: build.query<GetTemplatesResponse, GetTemplatesRequest>({
      providesTags: ['Templates'],
      transformResponse: (response: GetTemplatesResponse) => {
        return response;
      },
      query: ({ is_active, hospital_id }) => {
        const searchParams: any = {
          hospital_id,
        };
        if (is_active !== undefined) {
          searchParams.is_active = is_active;
        }
        const url = Utils.Query.addParamsToUrl(
          URL.AI_SCRIBE_TEMPLATES,
          searchParams
        );
        return {
          url,
          method: 'get',
        };
      },
    }),

    getSessions: build.query<GetSessionsResponse, GetSessionsRequest>({
      providesTags: ['Sessions'],
      transformResponse: (response: GetSessionsResponse) => {
        return response;
      },
      query: ({ user_id, limit = 20, offset = 0, hospital_id }) => {
        const searchParams: any = {
          limit,
          offset,
          hospital_id,
        };
        if (user_id) {
          searchParams.user_id = user_id;
        }
        const url = Utils.Query.addParamsToUrl(
          URL.AI_SCRIBE_SESSIONS,
          searchParams
        );
        return {
          url,
          method: 'get',
        };
      },
    }),

    uploadRecording: build.mutation<
      UploadRecordingResponse,
      UploadRecordingRequest
    >({
      invalidatesTags: ['Sessions'],
      query: ({ audio_file, hospital_id, template_id, duration_seconds }) => {
        const formData = new FormData();
        formData.append('audio_file', audio_file);
        formData.append('hospital_id', hospital_id.toString());
        if (template_id) {
          formData.append('template_id', template_id.toString());
        }
        if (duration_seconds) {
          formData.append('duration_seconds', duration_seconds.toString());
        }

        return {
          method: 'post',
          data: formData,
          url: URL.AI_SCRIBE_UPLOAD_RECORDING,
        };
      },
    }),
  }),
});

export const {
  useGetSessionQuery,
  useGetSessionsQuery,
  useGetTemplateQuery,
  useGetTemplatesQuery,
  useUploadFileMutation,
  useDeleteSessionMutation,
  useDeleteTemplateMutation,
  useProcessSessionMutation,
  useCreateTemplateMutation,
  useUpdateTemplateMutation,
  useUploadRecordingMutation,
} = aiScribeApi;

export * from './types';
